// Tile & prop art, generated procedurally per realm from small limited palettes.

import { RealmDef } from '../../shared/realms';
import { RNG, hashStr } from '../../shared/rng';
import { shade } from './sprites';

const S = 16;

function nc(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d')!];
}

function px(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, col: string): void {
  g.fillStyle = col;
  g.fillRect(x, y, w, h);
}

export interface Tileset {
  floors: HTMLCanvasElement[];
  decos: HTMLCanvasElement[];
  decoChance: number;
  wallTop: HTMLCanvasElement[];
  wallFace: HTMLCanvasElement[];
  blocks: HTMLCanvasElement[];
  liquid: HTMLCanvasElement[];
  void: HTMLCanvasElement[];
}

function speckle(g: CanvasRenderingContext2D, rng: RNG, cols: string[], count: number, w = S, h = S): void {
  for (let i = 0; i < count; i++) px(g, rng.int(0, w - 1), rng.int(0, h - 1), 1, 1, rng.pick(cols));
}

function floorTile(realm: RealmDef, v: number): HTMLCanvasElement {
  const [c, g] = nc(S, S);
  const [d, m, l, a] = realm.floorPal;
  const rng = new RNG(hashStr(realm.id) + v * 31);
  px(g, 0, 0, S, S, m);
  switch (realm.floor) {
    case 'grass': {
      speckle(g, rng, [d, d, l], 18);
      for (let i = 0; i < 3 + v; i++) {
        const x = rng.int(1, 14);
        const y = rng.int(2, 14);
        px(g, x, y, 1, 2, l);
        px(g, x + 1, y + 1, 1, 1, d);
      }
      break;
    }
    case 'stone': {
      // Flagstones
      const off = v % 2 === 0 ? 0 : 4;
      px(g, 0, 7, S, 1, d);
      px(g, 0, 15, S, 1, d);
      px(g, (3 + off) % S, 0, 1, 7, d);
      px(g, (11 + off) % S, 8, 1, 7, d);
      px(g, 0, 0, S, 1, l);
      px(g, 0, 8, S, 1, shade(m, 1.15));
      speckle(g, rng, [d, shade(m, 0.9), l], 8);
      break;
    }
    case 'sand': {
      speckle(g, rng, [d, l, l], 14);
      const y = rng.int(3, 12);
      for (let x = 0; x < S; x++) px(g, x, y + (x % 6 < 3 ? 0 : 1), 1, 1, shade(m, 0.92));
      if (v === 3) px(g, rng.int(2, 12), rng.int(2, 12), 2, 1, d);
      break;
    }
    case 'metal': {
      px(g, 0, 0, S, 1, l);
      px(g, 0, 0, 1, S, l);
      px(g, 0, 15, S, 1, d);
      px(g, 15, 0, 1, S, d);
      px(g, 2, 2, 1, 1, d);
      px(g, 13, 2, 1, 1, d);
      px(g, 2, 13, 1, 1, d);
      px(g, 13, 13, 1, 1, d);
      if (v === 1) {
        for (let x = 4; x < 12; x += 2) px(g, x, 4, 1, 8, d);
      } else if (v === 2) {
        px(g, 6, 7, 4, 2, a);
      }
      break;
    }
    case 'crystal': {
      for (let i = 0; i < S; i++) {
        px(g, i, (i + v * 3) % S, 1, 1, d);
        px(g, S - 1 - i, (i + v * 5) % S, 1, 1, l);
      }
      if (v === 2) {
        px(g, 7, 7, 2, 2, a);
      }
      speckle(g, rng, [d], 6);
      break;
    }
    case 'asphalt': {
      speckle(g, rng, [d, l], 22);
      if (v === 1) {
        px(g, 0, 7, 8, 1, '#a0a060');
      }
      if (v === 2) {
        px(g, 3, 2, 1, 12, shade(a, 0.5));
        px(g, 4, 3, 1, 10, d);
      }
      break;
    }
    case 'ice': {
      speckle(g, rng, [l, a], 10);
      for (let i = 0; i < 5; i++) px(g, 3 + i + v, 10 - i, 1, 1, a);
      if (v === 2) for (let i = 0; i < 8; i++) px(g, 2 + i, 13 - (i >> 1), 1, 1, d);
      break;
    }
    case 'warp': {
      speckle(g, rng, [d, l], 14);
      if (v % 2 === 0) {
        let y = rng.int(2, 13);
        for (let x = 0; x < S; x++) {
          y += rng.int(-1, 1);
          y = Math.max(1, Math.min(14, y));
          px(g, x, y, 1, 1, v === 2 ? a : l);
        }
      }
      break;
    }
    case 'hub': {
      px(g, 0, 0, S, 1, l);
      px(g, 0, 0, 1, S, l);
      px(g, 0, 15, S, 1, d);
      px(g, 15, 0, 1, S, d);
      if (v === 1) {
        px(g, 7, 3, 2, 10, d);
        px(g, 3, 7, 10, 2, d);
      } else if (v === 2) {
        px(g, 7, 7, 2, 2, l);
      }
      speckle(g, rng, [d], 4);
      break;
    }
  }
  return c;
}

function decoTile(realm: RealmDef, v: number): HTMLCanvasElement {
  const [c, g] = nc(S, S);
  const [d, , l, a] = realm.floorPal;
  const rng = new RNG(hashStr(realm.id) + 1000 + v);
  const x = rng.int(3, 10);
  const y = rng.int(3, 10);
  switch (realm.floor) {
    case 'grass':
      if (v === 0) {
        // Flower
        px(g, x, y, 1, 3, '#00a800');
        px(g, x - 1, y - 1, 3, 1, '#f8d878');
        px(g, x, y - 2, 1, 3, '#f8d878');
        px(g, x, y - 1, 1, 1, '#f83800');
      } else if (v === 1) {
        // Mushrooms
        px(g, x, y + 1, 2, 2, '#fcd8a8');
        px(g, x - 1, y, 4, 1, '#d82800');
      } else {
        // Tall grass
        for (let i = 0; i < 4; i++) px(g, x + i * 2, y + (i % 2), 1, 3, l);
      }
      break;
    case 'stone':
      if (v === 0) {
        // Bones
        px(g, x, y, 4, 1, '#d8d8d8');
        px(g, x + 1, y - 1, 1, 3, '#d8d8d8');
      } else if (v === 1) {
        // Skull
        px(g, x, y, 3, 3, '#d8d8d8');
        px(g, x, y + 1, 1, 1, '#000000');
        px(g, x + 2, y + 1, 1, 1, '#000000');
      } else {
        // Blood
        px(g, x, y, 3, 1, '#801010');
        px(g, x + 1, y + 1, 3, 1, '#801010');
      }
      break;
    case 'sand':
      if (v === 0) {
        // Rocks
        px(g, x, y, 3, 2, d);
        px(g, x, y, 2, 1, l);
      } else if (v === 1) {
        px(g, x, y, 4, 1, '#fcfcfc');
        px(g, x + 3, y - 1, 1, 3, '#fcfcfc');
      } else {
        // Glass shards
        px(g, x, y, 1, 2, '#a4e4fc');
        px(g, x + 3, y + 2, 1, 1, '#a4e4fc');
      }
      break;
    case 'metal':
      if (v === 0) {
        // Wires
        for (let i = 0; i < 6; i++) px(g, x + i, y + (i % 3 === 0 ? 1 : 0), 1, 1, '#f83800');
      } else if (v === 1) {
        // Goo
        px(g, x, y, 4, 2, '#58a800');
        px(g, x + 1, y + 2, 2, 1, '#58a800');
      } else {
        // Warning light
        px(g, x, y, 2, 2, '#f8b800');
      }
      break;
    case 'crystal':
      px(g, x, y, 2, 3, a);
      px(g, x, y, 1, 1, '#fcfcfc');
      if (v === 1) px(g, x + 3, y + 1, 1, 2, l);
      break;
    case 'asphalt':
      if (v === 0) {
        // Puddle reflecting neon
        px(g, x - 1, y, 6, 2, shade(a, 0.45));
        px(g, x, y, 2, 1, a);
      } else if (v === 1) {
        px(g, x, y, 3, 2, '#606060');
        px(g, x, y, 3, 1, '#00a8a8');
      } else {
        px(g, x, y, 1, 1, '#f878f8');
        px(g, x + 2, y + 1, 1, 1, '#00e8d8');
      }
      break;
    case 'ice':
      px(g, x, y, 3, 1, '#fcfcfc');
      px(g, x + 1, y + 1, 3, 1, d);
      break;
    case 'warp':
      px(g, x, y, 2, 2, a);
      px(g, x + 1, y, 1, 1, '#f8d878');
      if (v === 1) px(g, x + 3, y + 3, 1, 1, '#f878f8');
      break;
    case 'hub':
      px(g, x, y, 2, 1, a);
      break;
  }
  return c;
}

function wallFaceTile(realm: RealmDef, v: number): HTMLCanvasElement {
  const [c, g] = nc(S, S);
  const [d, m, l, a] = realm.wallPal;
  const rng = new RNG(hashStr(realm.id) + 2000 + v);
  px(g, 0, 0, S, S, m);
  switch (realm.wall) {
    case 'trees': {
      // A wall of trunks under a dark canopy
      px(g, 0, 0, S, S, d);
      for (const tx of [1, 6, 11]) {
        px(g, tx + (v & 1), 3, 4, 13, a);
        px(g, tx + (v & 1), 3, 1, 13, shade(a, 1.3));
        px(g, tx + 3 + (v & 1), 3, 1, 13, shade(a, 0.7));
      }
      for (let x = 0; x < S; x++) {
        const h = 2 + ((x * 7 + v * 3) % 5);
        px(g, x, 0, 1, h, m);
        px(g, x, 0, 1, Math.max(0, h - 2), l);
      }
      break;
    }
    case 'brick': {
      for (let row = 0; row < 4; row++) {
        const y = row * 4;
        px(g, 0, y + 3, S, 1, d);
        const off = row % 2 === 0 ? 0 : 4;
        for (let x = off; x < S + 8; x += 8) px(g, x % S, y, 1, 3, d);
        px(g, 0, y, S, 1, shade(m, 1.2));
      }
      if (v === 1) {
        px(g, 4, 4, 3, 3, d);
        px(g, 10, 9, 2, 1, a);
      }
      if (v === 2) {
        // Sconce
        px(g, 7, 5, 2, 3, '#403020');
        px(g, 7, 3, 2, 2, '#f8b800');
        px(g, 7, 2, 2, 1, '#f83800');
      }
      break;
    }
    case 'sandstone': {
      px(g, 0, 7, S, 1, d);
      px(g, 0, 15, S, 1, d);
      px(g, v * 5 + 3, 0, 1, 7, d);
      px(g, (v * 3 + 9) % S, 8, 1, 7, d);
      px(g, 0, 0, S, 1, l);
      px(g, 0, 8, S, 1, l);
      speckle(g, rng, [d, l], 12);
      if (v === 2) {
        // Hieroglyph
        px(g, 6, 2, 3, 1, a);
        px(g, 7, 3, 1, 3, a);
        px(g, 2, 10, 2, 3, a);
      }
      break;
    }
    case 'bulkhead': {
      px(g, 0, 0, S, 1, l);
      px(g, 0, 15, S, 1, d);
      px(g, 0, 5, S, 1, d);
      px(g, 0, 11, S, 1, d);
      if (v === 0) {
        for (let i = 0; i < 8; i++) {
          px(g, i * 2, 6, 1, 5, i % 2 ? '#f8b800' : '#201808');
        }
      } else if (v === 1) {
        px(g, 3, 7, 10, 3, '#101820');
        px(g, 4, 8, 3, 1, '#00e8d8');
        px(g, 9, 8, 2, 1, a);
      } else {
        px(g, 2, 2, 1, 1, l);
        px(g, 13, 2, 1, 1, l);
        px(g, 7, 7, 2, 2, a);
      }
      break;
    }
    case 'crystal': {
      for (let i = 0; i < S; i++) {
        px(g, i, Math.max(0, (S - i + v * 3) % S), 1, S, m);
      }
      for (let i = 0; i < 4; i++) {
        const x = rng.int(0, 12);
        const y = rng.int(0, 12);
        px(g, x, y, 1, 4, l);
        px(g, x + 1, y, 1, 3, a);
        px(g, x + 2, y + 2, 1, 2, d);
      }
      break;
    }
    case 'building': {
      px(g, 0, 0, S, S, m);
      for (let wy = 2; wy < S - 2; wy += 5) {
        for (let wx = 2; wx < S - 2; wx += 5) {
          const lit = rng.next();
          const col = lit < 0.35 ? '#f8d878' : lit < 0.5 ? a : lit < 0.6 ? '#f878f8' : d;
          px(g, wx, wy, 3, 3, col);
          px(g, wx, wy + 3, 3, 1, d);
        }
      }
      break;
    }
    case 'ice': {
      for (let i = 0; i < 6; i++) {
        const x = rng.int(0, 13);
        const y = rng.int(0, 12);
        px(g, x, y, 2, 4, l);
        px(g, x, y, 1, 2, a);
      }
      px(g, 0, 15, S, 1, d);
      speckle(g, rng, [d], 8);
      break;
    }
    case 'flesh': {
      px(g, 0, 15, S, 1, d);
      let y = rng.int(3, 10);
      for (let x = 0; x < S; x++) {
        y += rng.int(-1, 1);
        px(g, x, y, 1, 1, l);
        px(g, x, y + 1, 1, 1, d);
      }
      if (v === 1) {
        // An eye, regarding you
        px(g, 5, 5, 6, 4, '#fcfcfc');
        px(g, 7, 6, 2, 2, a);
        px(g, 7, 6, 1, 1, '#000000');
      }
      speckle(g, rng, [d, l], 6);
      break;
    }
    case 'hub': {
      px(g, 0, 0, S, 1, l);
      px(g, 0, 15, S, 1, d);
      px(g, 0, 7, S, 2, d);
      if (v === 1) px(g, 3, 7, 10, 2, a);
      px(g, 2, 2, 1, 1, l);
      px(g, 13, 2, 1, 1, l);
      break;
    }
  }
  // Shade toward the floor.
  g.globalAlpha = 0.3;
  px(g, 0, S - 3, S, 3, '#000000');
  g.globalAlpha = 1;
  return c;
}

function wallTopTile(realm: RealmDef, v: number): HTMLCanvasElement {
  const [c, g] = nc(S, S);
  const [d, m, l] = realm.wallPal;
  const rng = new RNG(hashStr(realm.id) + 3000 + v);
  if (realm.wall === 'trees') {
    px(g, 0, 0, S, S, d);
    for (let i = 0; i < 8; i++) {
      const x = rng.int(-2, 12);
      const y = rng.int(-2, 12);
      px(g, x, y, 5, 4, m);
      px(g, x + 1, y, 3, 1, l);
    }
    return c;
  }
  px(g, 0, 0, S, S, shade(d, 0.8));
  speckle(g, rng, [d, shade(d, 0.6)], 10);
  return c;
}

function blockSprites(realm: RealmDef): HTMLCanvasElement[] {
  const [d, m, l, a] = realm.wallPal;
  const out: HTMLCanvasElement[] = [];
  switch (realm.wall) {
    case 'trees': {
      for (let v = 0; v < 3; v++) {
        const [c, g] = nc(24, 34);
        const rng = new RNG(99 + v);
        px(g, 10, 22, 4, 12, '#503000');
        px(g, 10, 22, 1, 12, '#885020');
        px(g, 13, 22, 1, 12, '#301800');
        const blobs: [number, number, number, number, string][] = [
          [2, 10, 20, 14, d], [1, 12, 22, 9, d], [4, 4, 16, 12, m], [3, 8, 18, 10, m], [6, 2, 12, 8, l], [8, 0, 8, 4, l],
        ];
        for (const [x, y, w, h, col] of blobs) px(g, x, y, w, h, col);
        for (let i = 0; i < 26; i++) {
          const x = rng.int(3, 20);
          const y = rng.int(1, 22);
          if (g.getImageData(x, y, 1, 1).data[3]) px(g, x, y, 1, 1, rng.pick([d, m, l]));
        }
        out.push(c);
      }
      break;
    }
    case 'brick': {
      for (let v = 0; v < 2; v++) {
        const [c, g] = nc(16, 28);
        if (v === 0) {
          // Pillar
          px(g, 3, 4, 10, 22, m);
          px(g, 3, 4, 2, 22, l);
          px(g, 11, 4, 2, 22, d);
          px(g, 1, 0, 14, 4, m);
          px(g, 1, 0, 14, 1, l);
          px(g, 1, 26, 14, 2, d);
          for (let y = 8; y < 24; y += 6) px(g, 3, y, 10, 1, d);
        } else {
          // Tombstone
          px(g, 2, 12, 12, 16, m);
          px(g, 4, 9, 8, 3, m);
          px(g, 4, 9, 8, 1, l);
          px(g, 2, 12, 1, 16, l);
          px(g, 13, 12, 1, 16, d);
          px(g, 7, 15, 2, 8, d);
          px(g, 5, 17, 6, 2, d);
        }
        out.push(c);
      }
      break;
    }
    case 'sandstone': {
      {
        const [c, g] = nc(14, 30); // Obelisk
        px(g, 3, 4, 8, 24, m);
        px(g, 3, 4, 2, 24, l);
        px(g, 9, 4, 2, 24, d);
        px(g, 5, 0, 4, 4, l);
        px(g, 1, 27, 12, 3, d);
        px(g, 6, 8, 2, 3, a);
        px(g, 6, 14, 2, 1, a);
        px(g, 5, 18, 4, 1, a);
        out.push(c);
      }
      {
        const [c, g] = nc(14, 24); // Cactus
        const G = '#007800';
        const L = '#00b800';
        px(g, 5, 2, 4, 22, G);
        px(g, 6, 2, 1, 22, L);
        px(g, 1, 8, 2, 7, G);
        px(g, 1, 13, 5, 2, G);
        px(g, 11, 5, 2, 7, G);
        px(g, 8, 10, 5, 2, G);
        out.push(c);
      }
      {
        const [c, g] = nc(16, 20); // Glass spire
        px(g, 6, 0, 3, 20, '#a4e4fc');
        px(g, 6, 0, 1, 20, '#fcfcfc');
        px(g, 3, 8, 3, 12, '#3cbcfc');
        px(g, 9, 5, 3, 15, '#3cbcfc');
        out.push(c);
      }
      break;
    }
    case 'bulkhead': {
      {
        const [c, g] = nc(16, 20); // Crate
        px(g, 1, 4, 14, 16, '#a07830');
        px(g, 1, 4, 14, 1, '#d8a850');
        px(g, 1, 19, 14, 1, '#604010');
        px(g, 1, 4, 2, 16, '#604010');
        px(g, 13, 4, 2, 16, '#604010');
        for (let i = 0; i < 10; i++) px(g, 3 + i, 5 + i + 2, 1, 2, '#604010');
        out.push(c);
      }
      {
        const [c, g] = nc(16, 26); // Console
        px(g, 2, 2, 12, 24, m);
        px(g, 2, 2, 12, 1, l);
        px(g, 4, 4, 8, 7, '#001820');
        px(g, 5, 5, 3, 1, '#00e8d8');
        px(g, 5, 7, 5, 1, '#00a8a8');
        px(g, 5, 9, 2, 1, a);
        px(g, 4, 14, 2, 2, '#f83800');
        px(g, 7, 14, 2, 2, '#58f898');
        px(g, 10, 14, 2, 2, '#f8b800');
        px(g, 2, 24, 12, 2, d);
        out.push(c);
      }
      break;
    }
    case 'crystal': {
      for (let v = 0; v < 3; v++) {
        const [c, g] = nc(18, 30);
        const rng = new RNG(40 + v);
        const shards = 3 + v;
        for (let i = 0; i < shards; i++) {
          const x = rng.int(2, 12);
          const h = rng.int(12, 28);
          const w = rng.int(3, 4);
          px(g, x, 30 - h, w, h, m);
          px(g, x, 30 - h, 1, h, l);
          px(g, x + w - 1, 30 - h + 2, 1, h - 2, d);
          px(g, x + 1, 30 - h - 2, Math.max(1, w - 2), 2, a);
        }
        out.push(c);
      }
      break;
    }
    case 'building': {
      {
        const [c, g] = nc(12, 30); // Neon lamp
        px(g, 5, 6, 2, 24, '#484860');
        px(g, 1, 3, 10, 3, a);
        px(g, 1, 3, 10, 1, '#fcfcfc');
        px(g, 3, 28, 6, 2, d);
        out.push(c);
      }
      {
        const [c, g] = nc(16, 26); // Vending machine
        px(g, 2, 2, 12, 24, '#a81048');
        px(g, 2, 2, 12, 1, '#f878f8');
        px(g, 4, 4, 8, 12, '#a4e4fc');
        for (let i = 0; i < 9; i++) px(g, 5 + (i % 3) * 2, 5 + ((i / 3) | 0) * 3, 1, 2, ['#f83800', '#f8b800', '#58f898'][i % 3]);
        px(g, 4, 19, 8, 3, '#101018');
        out.push(c);
      }
      {
        const [c, g] = nc(16, 18); // Barrel
        px(g, 3, 4, 10, 14, '#384048');
        px(g, 3, 4, 10, 2, '#586068');
        px(g, 3, 9, 10, 1, '#f8b800');
        px(g, 3, 13, 10, 1, '#f8b800');
        px(g, 5, 2, 6, 2, '#58f898');
        out.push(c);
      }
      break;
    }
    case 'ice': {
      for (let v = 0; v < 2; v++) {
        const [c, g] = nc(16, 28);
        const rng = new RNG(70 + v);
        for (let i = 0; i < 4; i++) {
          const x = rng.int(2, 11);
          const h = rng.int(10, 26);
          for (let y = 0; y < h; y++) {
            const w = Math.max(1, Math.floor((y / h) * 5));
            px(g, x, 28 - h + y, w, 1, y % 5 === 0 ? l : m);
            px(g, x, 28 - h + y, 1, 1, a);
          }
        }
        out.push(c);
      }
      break;
    }
    case 'flesh': {
      {
        const [c, g] = nc(16, 30);
        px(g, 5, 6, 6, 24, m);
        px(g, 5, 6, 2, 24, l);
        px(g, 9, 6, 2, 24, d);
        px(g, 3, 0, 10, 8, l);
        px(g, 5, 2, 6, 4, '#fcfcfc');
        px(g, 7, 3, 2, 2, '#f83800');
        px(g, 3, 25, 10, 5, d);
        out.push(c);
      }
      {
        const [c, g] = nc(16, 24);
        for (let i = 0; i < 3; i++) {
          let x = 4 + i * 4;
          for (let y = 24; y > 4 + i * 2; y--) {
            x += (y + i) % 5 === 0 ? (i === 1 ? 1 : -1) : 0;
            px(g, x, y, 2, 1, y % 3 === 0 ? l : m);
          }
        }
        out.push(c);
      }
      break;
    }
    case 'hub': {
      const [c, g] = nc(14, 30);
      px(g, 3, 6, 8, 22, m);
      px(g, 3, 6, 2, 22, l);
      px(g, 9, 6, 2, 22, d);
      px(g, 5, 0, 4, 5, a);
      px(g, 6, 0, 2, 5, '#fcfcfc');
      px(g, 1, 27, 12, 3, d);
      out.push(c);
      break;
    }
  }
  return out;
}

function liquidFrames(realm: RealmDef): HTMLCanvasElement[] {
  const [d, m, l] = realm.liquidPal;
  const out: HTMLCanvasElement[] = [];
  for (let f = 0; f < 4; f++) {
    const [c, g] = nc(S, S);
    px(g, 0, 0, S, S, m);
    for (let i = 0; i < 3; i++) {
      const y = (i * 5 + f * 2) % S;
      const x = (i * 7 + f * 3) % S;
      px(g, x, y, 5, 1, l);
      px(g, (x + 9) % S, (y + 3) % S, 4, 1, d);
    }
    out.push(c);
  }
  return out;
}

function voidFrames(realm: RealmDef): HTMLCanvasElement[] {
  const [d, m, l] = realm.liquidPal;
  const out: HTMLCanvasElement[] = [];
  for (let f = 0; f < 4; f++) {
    const [c, g] = nc(S, S);
    px(g, 0, 0, S, S, d);
    const rng = new RNG(5 + f);
    for (let i = 0; i < 3; i++) px(g, rng.int(0, 15), rng.int(0, 15), 1, 1, i === 0 ? l : m);
    out.push(c);
  }
  return out;
}

const cache = new Map<string, Tileset>();

export function tilesetFor(realm: RealmDef): Tileset {
  let t = cache.get(realm.id);
  if (t) return t;
  t = {
    floors: [0, 1, 2, 3].map((v) => floorTile(realm, v)),
    decos: [0, 1, 2].map((v) => decoTile(realm, v)),
    decoChance: realm.floor === 'grass' ? 0.1 : realm.floor === 'hub' ? 0.015 : 0.05,
    wallFace: [0, 1, 2].map((v) => wallFaceTile(realm, v)),
    wallTop: [0, 1].map((v) => wallTopTile(realm, v)),
    blocks: blockSprites(realm),
    liquid: liquidFrames(realm),
    void: voidFrames(realm),
  };
  cache.set(realm.id, t);
  return t;
}

// ─── Interactive objects ───

const objCache = new Map<string, HTMLCanvasElement>();

export function objectSprite(type: string, state: number, sub: string, frame: number, realm: RealmDef): HTMLCanvasElement {
  const key = `${type}|${state}|${sub}|${frame % 4}|${type === 'stairsDown' || type === 'stairsUp' ? realm.id : ''}`;
  let c = objCache.get(key);
  if (c) return c;
  switch (type) {
    case 'stairsDown':
    case 'stairsUp':
      c = stairsSprite(type === 'stairsDown', realm);
      break;
    case 'portal':
      c = portalSprite(frame, sub === 'arena' ? ['#a81000', '#f83800', '#f8d878'] : sub === 'hub' ? ['#0058f8', '#3cbcfc', '#fcfcfc'] : ['#6844fc', '#d800cc', '#f8b8f8']);
      break;
    case 'gate':
      c = gateSprite(frame);
      break;
    case 'chest':
      c = chestSprite(state === 1, false);
      break;
    case 'stash':
      c = chestSprite(false, true);
      break;
    case 'shrine':
      c = shrineSprite(state === 1, sub, frame);
      break;
    default:
      c = chestSprite(false, false);
  }
  objCache.set(key, c);
  return c;
}

function stairsSprite(down: boolean, realm: RealmDef): HTMLCanvasElement {
  const [c, g] = nc(24, 24);
  const [d, m, l] = realm.wallPal;
  px(g, 0, 0, 24, 24, d);
  px(g, 0, 0, 24, 2, l);
  px(g, 0, 0, 2, 24, m);
  px(g, 22, 0, 2, 24, m);
  for (let i = 0; i < 5; i++) {
    const y = 3 + i * 4;
    const f = down ? 1 - i * 0.18 : 0.45 + i * 0.14;
    px(g, 3 + (down ? i : 0), y, 18 - (down ? i * 2 : 0), 3, shade(m === '#000000' ? '#585858' : m, f));
    px(g, 3 + (down ? i : 0), y, 18 - (down ? i * 2 : 0), 1, shade(l, f));
  }
  if (down) px(g, 7, 20, 10, 4, '#000000');
  return c;
}

function portalSprite(frame: number, cols: string[]): HTMLCanvasElement {
  const [c, g] = nc(22, 30);
  const [d, m, l] = cols;
  // Oval rim
  const ov = (rx: number, ry: number, col: string) => {
    for (let y = -ry; y <= ry; y++) {
      const w = Math.round(rx * Math.sqrt(Math.max(0, 1 - (y * y) / (ry * ry))));
      px(g, 11 - w, 14 + y, w * 2, 1, col);
    }
  };
  ov(11, 14, '#000000');
  ov(10, 13, d);
  ov(8, 11, m);
  ov(5, 8, l);
  ov(3, 5, '#fcfcfc');
  // Swirling sparks
  const rng = new RNG(frame * 13 + 7);
  for (let i = 0; i < 12; i++) {
    const a = rng.next() * 6.28 + frame * 0.8;
    const r = rng.next() * 9 + 1;
    px(g, Math.round(11 + Math.cos(a) * r * 0.85), Math.round(14 + Math.sin(a) * r * 1.2), 1, 1, i % 2 ? '#fcfcfc' : l);
  }
  return c;
}

function gateSprite(frame: number): HTMLCanvasElement {
  const [c, g] = nc(44, 46);
  const stone = '#585868';
  const stoneL = '#8888a0';
  const stoneD = '#303040';
  const ring = (rx: number, ry: number, col: string) => {
    for (let a = 0; a < 360; a += 2) {
      const r = (a * Math.PI) / 180;
      px(g, Math.round(22 + Math.cos(r) * rx), Math.round(22 + Math.sin(r) * ry), 2, 2, col);
    }
  };
  // Inner warp
  for (let y = -17; y <= 17; y++) {
    const w = Math.round(17 * Math.sqrt(1 - (y * y) / (17 * 17)));
    px(g, 22 - w, 22 + y, w * 2, 1, '#180830');
  }
  const rng = new RNG(frame * 3 + 11);
  for (let i = 0; i < 44; i++) {
    const a = rng.next() * 6.28 + frame * 0.4;
    const r = rng.next() * rng.next() * 16;
    const cols = ['#6844fc', '#d800cc', '#f878f8', '#3cbcfc', '#fcfcfc'];
    px(g, Math.round(22 + Math.cos(a) * r), Math.round(22 + Math.sin(a) * r), 2, 1, rng.pick(cols));
  }
  ring(19, 19, '#000000');
  ring(18, 18, stone);
  ring(19, 19, stoneD);
  ring(17, 17, stoneL);
  // Runes round the ring
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * 6.28 + 0.4;
    px(g, Math.round(22 + Math.cos(a) * 18), Math.round(22 + Math.sin(a) * 18), 2, 2, (i + frame) % 4 === 0 ? '#fcfcfc' : '#00e8d8');
  }
  // Pedestal
  px(g, 8, 40, 28, 6, stoneD);
  px(g, 8, 40, 28, 1, stoneL);
  return c;
}

function chestSprite(open: boolean, big: boolean): HTMLCanvasElement {
  const [c, g] = nc(18, 16);
  const wood = big ? '#6844fc' : '#885020';
  const woodL = big ? '#9878f8' : '#c08040';
  const woodD = big ? '#402880' : '#503000';
  const metal = big ? '#fcfcfc' : '#f8b800';
  px(g, 1, 2, 16, 14, '#000000');
  if (open) {
    px(g, 2, 3, 14, 4, woodD);
    px(g, 3, 4, 12, 3, '#180800');
    px(g, 2, 7, 14, 8, wood);
    px(g, 2, 7, 14, 1, woodL);
    px(g, 3, 5, 3, 1, '#f8d878');
    px(g, 8, 6, 2, 1, '#f8d878');
  } else {
    px(g, 2, 3, 14, 12, wood);
    px(g, 2, 3, 14, 1, woodL);
    px(g, 2, 8, 14, 1, woodD);
    px(g, 4, 3, 2, 12, metal);
    px(g, 12, 3, 2, 12, metal);
    px(g, 8, 7, 2, 3, metal);
  }
  return c;
}

const SHRINE_COLORS: Record<string, string> = {
  armor: '#bcbcbc', combat: '#f83800', mana: '#3cbcfc', speed: '#58f898', exp: '#f878f8', gold: '#f8b800',
};

function shrineSprite(used: boolean, sub: string, frame: number): HTMLCanvasElement {
  const [c, g] = nc(16, 24);
  const col = used ? '#404040' : SHRINE_COLORS[sub] ?? '#f8d878';
  px(g, 2, 20, 12, 4, '#303040');
  px(g, 2, 20, 12, 1, '#7c7c8c');
  px(g, 5, 10, 6, 10, '#505060');
  px(g, 5, 10, 1, 10, '#8c8c9c');
  px(g, 3, 8, 10, 2, '#7c7c8c');
  const off = used ? 0 : frame % 2;
  px(g, 5, 2 - off + 1, 6, 5, col);
  px(g, 6, 3 - off + 1, 2, 2, used ? '#585858' : '#fcfcfc');
  return c;
}
